import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import {
  createInvite,
  getSessionInvites,
  getCollaborators,
  cancelInvite,
  removeCollaborator,
  updateCollaboratorRole,
} from '../utils/firebase';
import { trackEvent } from '../utils/analytics';

function ShareDialog({ show, onHide, session, user }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [collaborators, setCollaborators] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (show && session && user) {
      trackEvent('share_dialog_opened');
      loadCollaboratorsAndInvites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, session, user]);

  const loadCollaboratorsAndInvites = async () => {
    setLoadingData(true);
    try {
      const [collabs, invites] = await Promise.all([
        getCollaborators(user.uid, session.id),
        getSessionInvites(user.uid, session.id),
      ]);
      setCollaborators(collabs);
      setPendingInvites(invites.filter((inv) => inv.status === 'pending'));
    } catch (err) {
      console.error('Error loading collaborators:', err);
      setError('Failed to load collaborators');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Don't allow inviting yourself
    if (email.toLowerCase() === user.email?.toLowerCase()) {
      setError("You can't invite yourself");
      return;
    }

    setLoading(true);
    try {
      await createInvite(
        user.uid,
        session.id,
        session.title || 'Untitled Project',
        email,
        role,
        user.email
      );
      trackEvent('collaborator_invited', { role });
      setSuccess(`Invite sent to ${email}`);
      setEmail('');
      loadCollaboratorsAndInvites();
    } catch (err) {
      console.error('Error creating invite:', err);
      setError(err.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await cancelInvite(inviteId, user.uid);
      loadCollaboratorsAndInvites();
    } catch (err) {
      console.error('Error canceling invite:', err);
      setError('Failed to cancel invite');
    }
  };

  const handleRemoveCollaborator = async (collaboratorUid) => {
    try {
      await removeCollaborator(user.uid, session.id, collaboratorUid);
      loadCollaboratorsAndInvites();
    } catch (err) {
      console.error('Error removing collaborator:', err);
      setError('Failed to remove collaborator');
    }
  };

  const handleRoleChange = async (collaboratorUid, newRole) => {
    try {
      await updateCollaboratorRole(user.uid, session.id, collaboratorUid, newRole);
      loadCollaboratorsAndInvites();
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role');
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('editor');
    setError('');
    setSuccess('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-people me-2"></i>
          Share Project
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          Invite others by email to collaborate on this project. They'll receive an invitation they can accept from their Projects drawer.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Form onSubmit={handleInvite} className="mb-4">
          <Form.Label className="fw-semibold">Invite by Email</Form.Label>
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <Form.Select
              style={{ width: '120px' }}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={loading}
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </Form.Select>
          </div>
          <Button type="submit" variant="primary" disabled={loading} className="w-100">
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="bi bi-send me-2"></i>
                Send Invite
              </>
            )}
          </Button>
          <Form.Text className="text-muted">
            <strong>Editor</strong> — can send messages and modify the project. <strong>Viewer</strong> — can only view.
          </Form.Text>
        </Form>

        {loadingData ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="secondary" />
          </div>
        ) : (
          <div>
            <div className="fw-semibold mb-2">People with Access</div>
            <ListGroup variant="flush" className="share-list">
              {/* Owner */}
              <ListGroup.Item className="d-flex align-items-center justify-content-between px-0">
                <div className="d-flex align-items-center gap-2">
                  <div className="collaborator-avatar">
                    {user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div className="fw-medium">{user.email}</div>
                    <small className="text-muted">You</small>
                  </div>
                </div>
                <Badge bg="secondary">Owner</Badge>
              </ListGroup.Item>

              {/* Collaborators */}
              {collaborators.map((collab) => (
                <ListGroup.Item
                  key={collab.uid}
                  className="d-flex align-items-center justify-content-between px-0"
                >
                  <div className="d-flex align-items-center gap-2">
                    <div className="collaborator-avatar">
                      {collab.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="fw-medium">{collab.email}</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Select
                      size="sm"
                      value={collab.role}
                      onChange={(e) => handleRoleChange(collab.uid, e.target.value)}
                      style={{ width: '100px' }}
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </Form.Select>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveCollaborator(collab.uid)}
                      title="Remove collaborator"
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}

              {/* Pending Invites */}
              {pendingInvites.map((invite) => (
                <ListGroup.Item
                  key={invite.id}
                  className="d-flex align-items-center justify-content-between px-0"
                >
                  <div className="d-flex align-items-center gap-2">
                    <div className="collaborator-avatar pending">
                      {invite.email?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="fw-medium">{invite.email}</div>
                      <small className="text-muted">
                        Pending — waiting for them to accept ({invite.role})
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleCancelInvite(invite.id)}
                    title="Cancel invite"
                  >
                    <i className="bi bi-x"></i>
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>

            {collaborators.length === 0 && pendingInvites.length === 0 && (
              <p className="text-muted text-center mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                Only you have access. Enter an email above to invite a collaborator.
              </p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ShareDialog;
